import type { CSSProperties, FunctionComponent } from 'react';
import InfoIcon from '@atlaskit/icon/core/information';
import { token } from '@atlaskit/tokens';
import { defineMessages, FormattedMessage } from 'react-intl';
import {
  useInfo,
  useSelectedWorkspace,
} from '../../Flow/ConsentContextProvider';

const messages = defineMessages({
  accessBodyWithOrganization: {
    id: 'oauth.twg.siteAccessNotice.accessBodyWithOrganization',
    defaultMessage:
      'Choosing this site ({site}) authorizes Teamwork Graph CLI to access any data that your account can access across all sites in the organization ({organization}).',
    description:
      'Notice shown near the TWG accept button explaining that site selection grants organization-wide site access when the selected organization name is known',
  },
  accessBodyWithoutOrganization: {
    id: 'oauth.twg.siteAccessNotice.accessBodyWithoutOrganization',
    defaultMessage:
      'Choosing this site ({site}) authorizes Teamwork Graph CLI to access any data that your account can access across all sites in the organization.',
    description:
      'Notice shown near the TWG accept button explaining that site selection grants organization-wide site access when the selected organization name is unavailable',
  },
});

const styles: Record<string, CSSProperties> = {
  notice: {
    alignItems: 'flex-start',
    color: 'var(--twg-muted)',
    display: 'flex',
    gap: '8px',
    maxWidth: '772px',
    width: '100%',
  },
  icon: {
    flexShrink: 0,
    marginTop: '2px',
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    margin: 0,
  },
};

const getDisplayOrganizationName = (name?: string) => {
  const trimmedName = name?.trim();
  if (!trimmedName || /^\{\{.*\}\}$/.test(trimmedName)) {
    return undefined;
  }
  return trimmedName;
};

const TwgSiteAccessNotice: FunctionComponent = () => {
  const { selectedWorkspace } = useSelectedWorkspace();
  const consentInfo = useInfo();

  if (!selectedWorkspace) {
    return null;
  }

  const displayOrganizationName = getDisplayOrganizationName(
    consentInfo.selectedOrgInfo?.name,
  );

  return (
    <div style={styles.notice}>
      <span style={styles.icon} aria-hidden="true">
        <InfoIcon
          label=""
          color={token('color.icon.information')}
          size="small"
        />
      </span>
      <p style={styles.text}>
        {displayOrganizationName ? (
          <FormattedMessage
            {...messages.accessBodyWithOrganization}
            values={{
              site: <strong>{selectedWorkspace}</strong>,
              organization: <strong>{displayOrganizationName}</strong>,
            }}
          />
        ) : (
          <FormattedMessage
            {...messages.accessBodyWithoutOrganization}
            values={{
              site: <strong>{selectedWorkspace}</strong>,
            }}
          />
        )}
      </p>
    </div>
  );
};

export default TwgSiteAccessNotice;
